import { StateField, StateEffect, Transaction } from '@codemirror/state';

export const setTermPositions = StateEffect.define();

export const termPositionsField = StateField.define({
    create() {
        return {
            positions: new Map(),
            version: 0
        };
    },
    
    update(value, tr) {
        for (let effect of tr.effects) {
            if (effect.is(setTermPositions)) {
                return {
                    positions: effect.value,
                    version: value.version + 1
                };
            }
        }
        
        const userEvent = tr.annotation(Transaction.userEvent);
        
        if (userEvent === 'claim-dependency.accept') {
            return {
                positions: new Map(),
                version: value.version
            };
        }
        
        if (tr.docChanged) {
            const newPositions = new Map();
            
            for (let [key, posArray] of value.positions) {
                const mappedArray = posArray.map(pos => ({
                    from: tr.changes.mapPos(pos.from),
                    to: tr.changes.mapPos(pos.to),
                    data: pos.data
                })).filter(pos => pos.from < pos.to);
                
                if (mappedArray.length > 0) {
                    newPositions.set(key, mappedArray);
                }
            }
            
            return {
                positions: newPositions,
                version: value.version
            };
        }
        
        return value;
    }
});

/**
 * Convert backend term data to position map for the StateField
 * @param {Map} termEntries - Term entries from backend
 * @param {Text} doc - CodeMirror document
 * @returns {Map} Position map for StateField
 */
export function convertTermsToPositions(termEntries, doc) {
    const positions = new Map();
    
    if (!termEntries || termEntries.size === 0) return positions;
    
    termEntries.forEach((termData, indexedKey) => {
        const termPositions = [];
        
        // Process parent term positions
        if (termData.abs_start_char !== undefined && termData.abs_end_char !== undefined) {
            termPositions.push({
                from: termData.abs_start_char,
                to: termData.abs_end_char,
                data: {
                    type: 'parent',
                    term: termData.parent_term,
                    warning: termData._warning
                }
            });
        }
        
        // Process child term positions with article errors
        if (termData.children) {
            Object.entries(termData.children).forEach(([childTerm, childData]) => {
                if (!childData.references) return;
                
                const { errors, abs_start_chars, abs_end_chars } = childData.references;
                if (!abs_start_chars || !abs_end_chars || !errors) return;
                
                for (let i = 0; i < errors.length; i++) {
                    const error = errors[i];
                    if (!error) continue;
                    
                    let from = abs_start_chars[i];
                    let to = abs_end_chars[i];
                    
                    // Adjust positions for article errors
                    if (error.error_start_char !== undefined && error.error_end_char !== undefined) {
                        const _claimNum = childData.references.claims[i];
                        const relativeStartAdjustment = error.error_start_char - childData.references.start_chars[i];
                        from = abs_start_chars[i] + relativeStartAdjustment;
                        to = error.error_end_char - childData.references.end_chars[i] + abs_end_chars[i];
                    }
                    
                    // Validate positions
                    if (from >= 0 && to <= doc.length && from < to) {
                        termPositions.push({
                            from,
                            to,
                            data: {
                                type: 'article_error',
                                term: childTerm,
                                error: error,
                                expected: error.expected || `the ${childTerm}`
                            }
                        });
                    }
                }
            });
        }
        
        if (termPositions.length > 0) {
            positions.set(indexedKey, termPositions);
        }
    });
    
    return positions;
}

/**
 * Helper to update term positions in an editor view
 * @param {EditorView} view - CodeMirror editor view
 * @param {Map} termEntries - Term entries from backend
 */
export function updateTermPositions(view, termEntries) {
    const positions = convertTermsToPositions(termEntries, view.state.doc);
    view.dispatch({
        effects: setTermPositions.of(positions)
    });
}